import { useCallback } from 'react'
import { trackError } from '@vend/utilities'
import { useVdFeatureManager } from '../../../../ng-compat/hooks'
import { SANDBOX_FLAG } from '../helpers/lspayConstants'

export enum TAG {
  lspay = 'lspay',
  sandbox = 'lspay_sandbox',
  production = 'lspay_production',
  payment = 'lspay_payment',
  refund = 'lspay_refund',
  cardNotPresentPayment = 'lspay_card_not_present_payment',
  serverDriven = 'lspay_server_driven',
}

export enum PRIORITY {
  low = 'lspay_low_priority',
  high = 'lspay_high_priority',
}

export type ErrorTrackingFunction = (
  message: string,
  error: string | Error,
  extraData?: object,
  lowPriorityCodes?: string[]
) => void

/**
 * A helper to track LSPAY errors easily
 *
 * @param sectionTag A tag to pass along every tracked error sent from this hook.
 * Other tags are added automatically (`lspay` and, if relevant, `lspay_sandbox`)
 *
 * @param data - optional - this data will be attached to every data produced by the returned trackError function
 *
 * @returns an error-tracking function for LSPAY errors, that takes
 * a message and an error (which will be added to the error's custom data),
 * and adds custom tags so we can filter out those LSPAY errors and find out
 * whether they happened on the prod or sandbox LSPAY account.
 * Use extraData to pass additional custom data.
 */
export const useTrackLSPAYError = (
  sectionTag: TAG.payment | TAG.refund | TAG.cardNotPresentPayment,
  data?: object
): ErrorTrackingFunction => {
  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)

  const trackError = useCallback(
    (
      message: string,
      error: any,
      extraData?: object,
      lowPriorityCodes?: string[]
    ) => {
      // default priority is high
      let priority = PRIORITY.high

      // if the error had a code which was low priority we change the priority to be low
      if (error?.code && lowPriorityCodes?.some(code => error.code === code)) {
        priority = PRIORITY.low
      }

      // combine the section tag and the standard ones
      const tags: string[] = [
        sectionTag,
        // tags added automatically
        priority,
        TAG.lspay,
        isSandbox ? TAG.sandbox : TAG.production,
      ]

      const metadata = { ...data, error }
      trackError(message, {
        data: { ...extraData, metadata },
        tags,
      })
    },
    [isSandbox, sectionTag, data]
  )

  return trackError
}

/**
 * Helper to track Lspay Server driven errors
 *
 * @param message A message to describe the error
 * @param error The error object
 * @param metadata Additional metadata to attach to the error
 * @param sectionTag The section tag to be added to the error
 */
export const trackLSPayServerError = (
  message: string,
  error: object,
  metadata: object,
  sectionTag: TAG.payment | TAG.refund
) => {
  const tags: string[] = [
    TAG.lspay,
    sectionTag,
    TAG.serverDriven,
    PRIORITY.high,
  ]
  trackError(message, { error, metadata }, tags)
}
